import logError from 'src/data/services/logError';
import showAlert from 'src/core/utils/showAlert';
import errorHandler from 'src/core/utils/errorHandler';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	View,
	TouchableOpacity,
	Text,
	Image,
	StyleSheet,
	Platform,
	LayoutAnimation,
	ActivityIndicator,
	TextInput,
} from 'react-native';
import { Col, Grid } from 'react-native-easy-grid';
import CheckBoxList from 'src/presentation/components/organisms/forms/CheckBoxList';
import RadioListField from 'src/presentation/components/organisms/forms/RadioListField';
import SearchableSelectField from 'src/presentation/components/organisms/forms/SearchableSelectField';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import KeyboardSpacer from 'src/presentation/components/atoms/KeyboardSpacer';
import FieldError from 'src/presentation/components/atoms/FieldError';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Images from 'src/core/utils/Images';
import { choiceReferences } from 'src/core/utils/formChoiceReferences';
import _ from 'lodash';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useFreshEvent } from 'src/presentation/hooks/useFreshEvent';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import { isApiError } from 'src/core/models/api/ApiError';
import firstErrorMessage from 'src/core/utils/firstErrorMessage';
import OnBehalfRegistrations from 'src/presentation/components/organisms/contentblocks/OnBehalfRegistrations';
import type { ApiContentBlockForm } from 'src/core/models/api/ApiContentBlock';
import type { ApiFormAnswer, ApiFormQuestion } from 'src/core/models/api/shared/ApiFormQuestion';
import type { ApiFormSubmission } from 'src/core/models/api/ApiFormSubmission';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

const tintColors: Record<number, string> = {
	0: Colors.greyText,
	1: Colors.red,
	2: Colors.orange,
	3: Colors.yellowAccent,
	4: Colors.green,
	5: Colors.greenAlt,
};

const MODE_QUESTION_NAME = 'on_behalf_mode';

// The e-mail question is recognised by its name or label, the same way for the account prefill and the
// on-behalf success message.
function findEmailQuestion(questions: ApiFormQuestion[]) {
	return questions.find((question) => /e-?mail/i.test(question.name) || /e-?mail/i.test(question.label ?? ''));
}

interface ContentBlockFormProps {
	data: ApiContentBlockForm;
	first?: boolean;
	last?: boolean;
	firstOfType?: boolean;
	route?: RouteProp<RootStackParamList>;
	onFieldFocus?: (input: TextInput) => void;
}

type FormQuestionWithKey = ApiFormQuestion & { key: string };

const TEXT_FIELD_TYPES = ['text', 'textarea', 'email', 'number', 'date'];

function ContentBlockForm({ data, first, last, firstOfType, route, onFieldFocus }: ContentBlockFormProps) {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const eventStore = useEventStore();
	const authStore = useAuthenticationStore();
	const formStore = useFormStore();
	const notificationStore = useNotificationStore();
	const isCancelLoading = useFormStore((s) => s.isCancelLoading);
	const isRegistrationForm = data.data.registration_required === '1' || data.data.registration_required === true;
	// Sign-up forms gate on the event's capacity, which only changes server-side.
	useFreshEvent(isRegistrationForm ? eventStore.activeEventUuid : undefined);

	// Flips on each rating selection so the component re-renders (the value itself lives in answersRef).
	const [, forceRatingRerender] = useState(false);
	const [formLoading, setFormLoading] = useState(false);
	const [validateFields, setValidateFields] = useState<Record<string, boolean>>({});
	const [fieldFocus, setFieldFocusState] = useState<Record<string, boolean>>({});
	// The questions and the user's own answers load side by side; the form shows once both are in, or it
	// opens empty and then jumps open when the answers arrive.
	const [isFormLoading, setIsFormLoading] = useState(true);
	const [isAnswersLoading, setIsAnswersLoading] = useState(true);
	// Set when the form definition could not be fetched (offline / API down): the form is unfillable, so we show
	// a "needs connection" placeholder instead of empty fields + a submit button.
	const [loadFailed, setLoadFailed] = useState(false);
	// Set when the form is closed (unpublished / past its publish-till): hide the fields + submit button.
	const [formClosed, setFormClosed] = useState(false);
	// Set when looking up the user's own registration failed for a reason other than "not found": we then do not
	// know whether they are already registered, so the full-event gate must not fire.
	const [submissionUnknown, setSubmissionUnknown] = useState(false);

	const [questions, setQuestions] = useState<ApiFormQuestion[]>([]);
	// Bumping this re-keys (and thus remounts) the rendered fields so their uncontrolled `defaultValue` re-reads
	// from answersRef, needed when answers/prefill arrive after mount.
	const [answersVersion, setAnswersVersion] = useState(0);
	const answersRef = useRef<Record<string, ApiFormAnswer>>({});
	const questionsRef = useRef<ApiFormQuestion[]>([]);
	const inputRefs = useRef<Record<string, TextInput | null>>({});
	const pushtokenRef = useRef<string>('');
	const previousSubmissionRef = useRef<string | number | null>(null);
	// Name of the single question we filled with the account's e-mail address.
	const accountEmailFieldRef = useRef<string | null>(null);
	const previousTokenRef = useRef<string>(authStore.userToken);
	// Just logged in from the form gate: the answers for the new account are not asked for yet.
	const isLoading = isFormLoading || isAnswersLoading || (previousTokenRef.current === '' && authStore.userToken !== '');
	// The focus that comes with mounting is skipped, so it does not fetch on top of the mount fetch.
	const initialFocusRef = useRef(true);
	// A focus and a foreground can both start a fetch before the previous one is back; only the last one
	// started may touch the screen, or an older answer overwrites a newer question list.
	const formFetchRef = useRef(0);

	// Registering someone else: the answers are theirs, so nothing of the user's own registration is prefilled,
	// locked, sent along or stored. The ref serves the async callbacks that outlive a render.
	const allowOnBehalf = data.data.cb_allow_on_behalf === true && authStore.userToken !== '';
	const [onBehalf, setOnBehalf] = useState(false);
	const onBehalfRef = useRef(false);
	const [onBehalfListKey, setOnBehalfListKey] = useState(0);
	const modeQuestion = useMemo<ApiFormQuestion>(
		() => ({
			name: MODE_QUESTION_NAME,
			type: 'radio',
			required: false,
			label: Translations.onBehalfQuestion,
			sortOrder: 0,
			options: [
				{ name: Translations.onBehalfSelf, limit: null, total_submissions: 0 },
				{ name: Translations.onBehalfOther, limit: null, total_submissions: 0 },
			],
		}),
		[],
	);
	const modeAnswers = useMemo(
		() => ({ [MODE_QUESTION_NAME]: onBehalf ? Translations.onBehalfOther : Translations.onBehalfSelf }),
		[onBehalf],
	);

	useEffect(() => {
		if (isRegistrationForm && authStore.userToken === '') {
			formStore.formParams = (route?.params as Record<string, unknown> | undefined) ?? null;
			formStore.loginFromForm = true;
			navigation.navigate(Constants.screenFormLogin, {});
			if (firstOfType) {
				navigation.navigate(Constants.screenFormLogin, {});
			}
		}
		getAnswers();
		getForm();
		getUserPushToken().catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const previousToken = previousTokenRef.current;
		previousTokenRef.current = authStore.userToken;
		// The user just logged in from the form gate, reload their submission/prefill so the form reflects their
		// account without a manual reopen.
		if (previousToken === '' && authStore.userToken !== '') {
			getAnswers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.userToken]);

	useEffect(() => {
		// Prefill the email field once the profile is available (e.g.
		if (prefillEmailFromProfile()) {
			setAnswersVersion((version) => version + 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.userProfile?.email, questions]);

	const getUserPushToken = async () => {
		try {
			pushtokenRef.current = (await notificationStore.getPushToken()) ?? '';
		} catch {
			pushtokenRef.current = '';
		}
	};

	function validateForm(formResults: Record<string, ApiFormAnswer>) {
		const required = questions.filter((q) => q.required);
		const missing = required.map((q) => q.name.replace(/[^a-zA-Z0-9]+/g, '-')).filter((name) => !formResults[name]);
		return missing;
	}

	const cancelSubmission = () => {
		const token: string | number =
			authStore.userToken !== '' ? authStore.userToken : (previousSubmissionRef.current ?? '');

		// Cancel comes first here, where Alert.alert had the destructive button in front. AlertHelper
		// exists to keep that order the same everywhere, and this was the one place that differed --
		// it now matches logging out and deleting an account.
		AlertHelper.showConfirmAlert({
			title: Translations.alertUnsubscribeTitle,
			message: Translations.alertUnsubscribeMessage,
			confirmText: Translations.textUnsubscribe,
			confirmButtonStyle: 'destructive',
			onConfirm: () => formStore.cancelSubmission(data.id, String(token), navigation),
			// Was dismissible before this migration and should stay that way: react-native's own
			// Android default is false, so it has to be said out loud.
			cancelable: true,
		});
	};

	const resetAnswers = () => {
		answersRef.current = {};
		accountEmailFieldRef.current = null;
		createFormResults(questionsRef.current);
		setValidateFields({});
		setAnswersVersion((version) => version + 1);
	};

	const switchMode = (toOnBehalf: boolean) => {
		if (toOnBehalf === onBehalfRef.current) {
			return;
		}
		onBehalfRef.current = toOnBehalf;
		setOnBehalf(toOnBehalf);
		resetAnswers();
		if (!toOnBehalf) {
			getAnswers();
		}
	};

	const sendAndClearForm = () => {
		const formResults = answersRef.current;
		const onBehalfSubmit = onBehalfRef.current;
		const validation = validateForm(formResults);
		const userToken = authStore.userToken || null;

		setFormLoading(true);

		// No separate "empty form" alert: an empty form trips the required-field validation below, which shows the
		// inline messages, so a generic empty-form popup would be redundant noise.
		if (validation.length > 0) {
			setFormLoading(false);
			errorHandler(Translations.errorRequiredFields, null);
			validation.forEach((field) => {
				const fieldName = field + 'validate';
				setValidateFields((prev) => ({ ...prev, [fieldName]: true }));
			});
			return;
		}

		formStore
			.submit({
				formId: data.id,
				answers: formResults,
				// The picked programme parts by their own id, next to the answer itself, so the backend never has
				// to recognise a choice by its title (#404).
				choices: choiceReferences(data.data.cb_form_questions ?? [], formResults),
				pushToken: pushtokenRef.current,
				userToken,
				...(onBehalfSubmit ? { onBehalf: true } : { previousSubmission: previousSubmissionRef.current }),
			})
			.then((responseData) => {
				try {
					if (responseData.status === 'ok' && onBehalfSubmit) {
						// Never saveForm here: the stored submission is what the app shows as the user's own
						// registration and QR.
						const emailQuestion = findEmailQuestion(questionsRef.current);
						const email = emailQuestion ? formResults[emailQuestion.name] : null;
						showAlert({
							title: Translations.successFormReceivedTitle,
							message:
								typeof email === 'string' && email.length > 0
									? Translations.onBehalfSuccessMessage({ email })
									: Translations.successFormReceivedMessage,
						});
						resetAnswers();
						setOnBehalfListKey((key) => key + 1);
					} else if (responseData.status === 'ok' && responseData.result) {
						formStore.saveForm(responseData.result);
						showAlert({
							title: Translations.successFormReceivedTitle,
							message: Translations.successFormReceivedMessage,
							onConfirm: () => navigation.pop(),
							cancelable: false,
						});
					} else {
						const message = firstErrorMessage(responseData.error);
						if (message) {
							errorHandler(message, Translations.errorFormContactNote);
						} else {
							errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
						}
					}
					setFormLoading(false);
				} catch (err) {
					console.error(err);
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
					setFormLoading(false);
				}
			})
			.catch((err: unknown) => {
				// A refused submit (full event, closed breakout, validation) comes back as a 422, which axios rejects, so
				// the server message sits on the error response instead of in the resolved body.
				if (isApiError(err) && err.response?.status === 429) {
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTooManyAttempts);
					setFormLoading(false);
					return;
				}

				const message = isApiError(err) ? firstErrorMessage(err.response?.data?.error) : null;
				if (message) {
					errorHandler(message, Translations.errorFormContactNote);
				} else {
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
				}
				setFormLoading(false);
			});
	};

	function parseFormFields(formData: ApiFormQuestion[]): FormQuestionWithKey[] {
		const sorted = _.orderBy(formData, [(field) => field.sortOrder], ['asc']);
		return sorted.map((obj, idx) => ({ ...obj, key: String(idx) }));
	}

	const createFormResults = (arraylist: ApiFormQuestion[]) => {
		if (_.size(answersRef.current)) return;
		const currentState = answersRef.current;
		const fieldToAdd: Record<string, null> = {};
		arraylist.forEach((object) => {
			const formName = object.name.replace(/[^a-zA-Z0-9]+/g, '-');
			fieldToAdd[formName] = null;
		});
		answersRef.current = Object.assign(currentState, fieldToAdd);
	};

	function renderFormFields(formData: ApiFormQuestion[]) {
		const fields = parseFormFields(formData);
		// The keyboard's next key walks these, skipping the locked account e-mail.
		const typable = fields
			.filter((field) => TEXT_FIELD_TYPES.includes(field.type) && field.name !== accountEmailFieldRef.current)
			.map((field) => field.key);
		return fields.map((object) => renderInputByFieldType(object, typable));
	}

	function renderInputByFieldType(object: FormQuestionWithKey, typable: string[]) {
		const question = object.name;

		switch (object.type) {
			// Single-line text-like inputs.
			case 'text':
			case 'textarea':
			case 'email':
			case 'number':
			case 'date': {
				// The registration belongs to the signed-in account and the confirmation with the personal QR goes to its
				// address, so the field we filled from the profile is read-only.
				const isAccountEmail = accountEmailFieldRef.current === object.name;
				const nextField = typable[typable.indexOf(object.key) + 1];

				return (
					<View key={object.key} style={styles.fieldBlock}>
						<View style={[styles.inputWrapper, fieldFocus[object.key] ? styles.itemstyleFocussed : styles.itemstyle]}>
							<Text style={[styles.inputLabel, fieldFocus[object.key] ? styles.labelFocussed : styles.label]}>
								{object.label}
								{object.required ? '*' : null}
							</Text>
							<TextInput
								defaultValue={
									typeof answersRef.current[object.name] === 'string' ? (answersRef.current[object.name] as string) : undefined
								}
								editable={!isAccountEmail}
								keyboardType={object.type === 'email' ? 'email-address' : object.type === 'number' ? 'numeric' : 'default'}
								placeholder={object.label}
								placeholderTextColor={Colors.lightGrey}
								ref={(input) => {
									inputRefs.current[object.key] = input;
								}}
								returnKeyType={nextField ? 'next' : 'done'}
								selectionColor={Colors.secondary}
								style={[styles.input, isAccountEmail ? styles.inputLocked : null]}
								submitBehavior={nextField ? 'submit' : 'blurAndSubmit'}
								testID={isAccountEmail ? 'account-email-input' : undefined}
								onBlur={() => setFocus(object.key)}
								onChangeText={(text) => setInputFromFieldToFormResults(text, object.name)}
								onFocus={() => {
									setFocus(object.key);
									const input = inputRefs.current[object.key];
									if (input) onFieldFocus?.(input);
								}}
								onSubmitEditing={() => (nextField ? inputRefs.current[nextField]?.focus() : undefined)}
							/>
						</View>
						{isAccountEmail && <Text style={styles.fieldHint}>{Translations.textEmailFromAccount}</Text>}
						{validateFields[(object.name + 'validate').replace(/[^a-zA-Z0-9]+/g, '-')] && (
							<FieldError message={Translations.errorFieldRequired} />
						)}
					</View>
				);
			}

			case 'checkbox':
				return (
					<View key={object.key} style={styles.fieldBlock}>
						<CheckBoxList
							answers={answersRef.current}
							data={object}
							error={
								validateFields[(object.name + 'validate').replace(/[^a-zA-Z0-9]+/g, '-')]
									? Translations.errorFieldRequired
									: undefined
							}
							formID={data.id}
							name={object.name}
							values={answersRef.current[object.name]}
							onCheck={(input) => setInputFromFieldToFormResults(input, object.name.replace(/[^a-zA-Z0-9]+/g, '-'))}
						/>
					</View>
				);

			case 'select':
			case 'radio':
				if (object.searchable === true) {
					return (
						<View key={object.key} style={styles.fieldBlock}>
							<SearchableSelectField
								answers={answersRef.current}
								data={object}
								error={
									validateFields[(object.name + 'validate').replace(/[^a-zA-Z0-9]+/g, '-')]
										? Translations.errorFieldRequired
										: undefined
								}
								name={object.name}
								onSwitch={(input) => setInputFromFieldToFormResults(input, object.name.replace(/[^a-zA-Z0-9]+/g, '-'))}
							/>
						</View>
					);
				}
				return (
					<View key={object.key} style={styles.fieldBlock}>
						<RadioListField
							answers={answersRef.current}
							data={object}
							error={
								validateFields[(object.name + 'validate').replace(/[^a-zA-Z0-9]+/g, '-')]
									? Translations.errorFieldRequired
									: undefined
							}
							formID={data.id}
							name={object.name}
							onSwitch={(input) => setInputFromFieldToFormResults(input, object.name.replace(/[^a-zA-Z0-9]+/g, '-'))}
						/>
					</View>
				);

			case 'rating':
				return (
					<View key={object.key} style={styles.fieldBlock}>
						<View style={[styles.itemStyleRatingLabel, { borderBottomWidth: 1, borderBottomColor: Colors.greyBorder }]}>
							<Text style={[styles.label, styles.ratingLabel]}>
								{object.label}
								{object.required ? '*' : null}
							</Text>
						</View>
						<View style={styles.itemStyleRating}>
							<Grid>
								{[1, 2, 3, 4, 5].map((rating) => (
									<Col key={rating} style={styles.smiley}>
										<TouchableOpacity onPressIn={() => setRating(rating, question)}>
											<Image
												resizeMode="cover"
												source={
													rating === 1
														? Images.smiley1
														: rating === 2
															? Images.smiley2
															: rating === 3
																? Images.smiley3
																: rating === 4
																	? Images.smiley4
																	: Images.smiley5
												}
												style={[
													{
														tintColor: answersRef.current[question] === rating ? tintColors[rating] : tintColors[0],
													},
													styles.smileyIcon,
												]}
											/>
										</TouchableOpacity>
									</Col>
								))}
							</Grid>
						</View>
						{validateFields[(object.name + 'validate').replace(/[^a-zA-Z0-9]+/g, '-')] && (
							<FieldError message={Translations.errorFieldRequired} />
						)}
					</View>
				);

			default:
				return null;
		}
	}

	function setFocus(objectKey: string) {
		setFieldFocusState((prev) => ({
			...prev,
			[objectKey]: !prev[objectKey],
		}));
	}

	function setInputFromFieldToFormResults(input: ApiFormAnswer, question: string) {
		answersRef.current = Object.assign({}, answersRef.current, { [question]: input });
	}

	function setRating(value: number, question: string) {
		// Force a re-render on each selection.
		forceRatingRerender((flag) => !flag);
		setInputFromFieldToFormResults(value, question);
	}

	const prefillEmailFromProfile = (): boolean => {
		const email = authStore.userProfile?.email;
		// Only prefill for a logged-in user who has no submission yet, a real submission already carries their
		// answer, and we must never clobber what the user typed.
		if (!email || previousSubmissionRef.current || onBehalfRef.current) {
			return false;
		}
		const emailField = findEmailQuestion(questionsRef.current);
		if (!emailField) {
			return false;
		}
		const current = answersRef.current[emailField.name];
		if (typeof current === 'string' && current.length > 0) {
			return false;
		}
		answersRef.current = Object.assign({}, answersRef.current, { [emailField.name]: email });
		accountEmailFieldRef.current = emailField.name;
		return true;
	};

	const getForm = ({ refresh = false }: { refresh?: boolean } = {}) => {
		const fetchId = ++formFetchRef.current;

		formStore
			.getForm({ eventUuid: eventStore.activeEventUuid, formId: data.id })
			.then((json) => {
				if (fetchId !== formFetchRef.current) {
					return;
				}

				try {
					const publishTill = json.data?.cb_form_publish_till;
					const isClosed =
						json.published === false ||
						(typeof publishTill === 'string' && publishTill.length > 0 && new Date(publishTill) < new Date());
					if (isClosed) {
						// The closed-form placeholder (rendered below) already conveys this; no blocking alert.
						setFormClosed(true);
						setIsFormLoading(false);
					} else {
						const fetched = json.data.cb_form_questions ?? [];
						questionsRef.current = fetched;
						setQuestions(fetched);
						// A refresh that arrives after a failed first load is the recovery: the questions are back, so
						// the error state has to go with them.
						setLoadFailed(false);
						createFormResults(fetched);
						if (prefillEmailFromProfile()) {
							setAnswersVersion((version) => version + 1);
						}
						setIsFormLoading(false);
					}
				} catch (err) {
					console.error('Form fetch error:', err);
					failed(err);
				}
			})
			.catch(failed);

		// A first load that fails leaves nothing to show, so it becomes the error state. A refresh that fails
		// (offline, API down) keeps the questions already on screen: they are still fillable.
		function failed(err: unknown) {
			console.error(err);

			if (fetchId !== formFetchRef.current) {
				return;
			}

			if (!refresh) {
				setLoadFailed(true);
			}
			setIsFormLoading(false);
		}
	};

	// The beheer edits this form while participants have it open, and nothing else on this screen re-reads it:
	// without this, someone fills in the question list from the moment they opened the screen.
	useAppStateScreenListener({
		onFocussed: () => {
			// The first focus arrives with the mount, which fetches the form already.
			if (initialFocusRef.current) {
				initialFocusRef.current = false;
				return;
			}
			getForm({ refresh: true });
		},
		onForegrounded: () => getForm({ refresh: true }),
	});

	const getAnswers = () => {
		const submission = formStore.getSubmissionByFormID(data.id);

		if (authStore.userToken) {
			getSubmissionByUser(data.id, authStore.userToken);
		} else {
			setIsAnswersLoading(false);
			if (!submission) return;
			applySubmissionAnswers(submission);
		}
	};

	const applySubmissionAnswers = (submission: Partial<ApiFormSubmission>) => {
		if (onBehalfRef.current) {
			return;
		}
		const answers = submission.answers ?? {};
		answersRef.current = answers;
		previousSubmissionRef.current = (submission.id as string | number | null) ?? null;
		// The submission's own answers replace any prefill (the form load can win the race with the submission
		// lookup), so nothing is account-filled any more and nothing stays locked.
		accountEmailFieldRef.current = null;
		setAnswersVersion((version) => version + 1);

		for (const key in answers) {
			if (Object.prototype.hasOwnProperty.call(answers, key)) {
				setInputFromFieldToFormResults(answers[key] ?? null, key);
			}
		}
	};

	const getSubmissionByUser = (ID: string | number, token: string) => {
		setIsAnswersLoading(true);
		formStore
			.getSubmission({ formId: ID, id: token })
			.then((responseData) => {
				try {
					if (!_.isEmpty(responseData)) {
						applySubmissionAnswers(responseData);
					} else {
						const submission = formStore.getSubmissionByFormID(ID);
						if (submission) {
							applySubmissionAnswers(submission);
						} else if (prefillEmailFromProfile()) {
							setAnswersVersion((version) => version + 1);
						}
					}
					setIsAnswersLoading(false);
				} catch {
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
					setIsAnswersLoading(false);
				}
			})
			.catch((err: unknown) => {
				console.error(err);
				// A 404 means the user has no submission yet, still prefill their email.
				if (!isApiError(err) || err.response?.status !== 404) {
					setSubmissionUnknown(true);
				}
				if (prefillEmailFromProfile()) {
					setAnswersVersion((version) => version + 1);
				}
				setIsAnswersLoading(false);
			});
	};

	if (isRegistrationForm && authStore.userToken === '') {
		return (
			<View style={{ flex: 1 }}>
				<Text style={styles.formTitle}>{Translations.errorFormLoginRequired}</Text>
			</View>
		);
	}

	// API-1: the event payload tells us the event reached its capacity.
	const activeEvent = eventStore.eventData?.find((event) => event.uuid === eventStore.activeEventUuid);
	const isEventFull =
		isRegistrationForm && activeEvent?.is_full === true && !previousSubmissionRef.current && !submissionUnknown;

	if (Platform.OS === 'ios') {
		LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
	}

	return (
		<View
			style={[
				last ? styles.last : null,
				first ? styles.first : null,
				{ padding: Dimens.padding16 },
				isLoading || loadFailed || formClosed || isEventFull ? styles.flexFill : null,
			]}
		>
			{isLoading ? (
				<View style={styles.formLoading}>
					<Loader small={true} />
				</View>
			) : formClosed ? (
				<EmptyState
					icon="lock-closed-outline"
					message={Translations.errorFormClosedMessage}
					title={Translations.errorFormClosedTitle}
				/>
			) : loadFailed ? (
				// Before the full-event state: a failed load means the user's own registration is unknown too, so
				// claiming the event is full could wrongly lock out a registrant.
				<EmptyState
					icon="cloud-offline-outline"
					message={
						useNavigationStore.getState().isConnected
							? Translations.errorFormLoadFailed
							: Translations.errorFormNeedsConnection
					}
					title={Translations.errorSomethingWentWrongShort}
				/>
			) : isEventFull ? (
				<EmptyState
					icon="people-outline"
					message={Translations.errorEventFullMessage}
					title={Translations.errorEventFullTitle}
				/>
			) : (
				<View style={{ padding: Dimens.padding0, margin: Dimens.margin0 }}>
					<Text style={[styles.formTitle, { color: eventStore.activeTheme.heading_text_color }]}>
						{data.data.cb_form_title}
					</Text>

					{/* The intro the beheerder writes above the questions. It was stored
					    and never rendered anywhere, so what they typed was invisible in
					    the app and on the web (#404). */}
					{data.data.cb_form_intro ? <Text style={styles.formIntro}>{data.data.cb_form_intro}</Text> : null}

					{allowOnBehalf ? (
						<View style={styles.fieldBlock}>
							{authStore.userProfile?.email ? (
								<Text style={styles.loggedInAs}>
									{Translations.onBehalfLoggedInAs({
										name:
											[authStore.userProfile.firstname, authStore.userProfile.lastname].filter(Boolean).join(' ') ||
											authStore.userProfile.email,
										email: authStore.userProfile.email,
									})}
								</Text>
							) : null}
							<RadioListField
								answers={modeAnswers}
								data={modeQuestion}
								name={MODE_QUESTION_NAME}
								onSwitch={(value) => switchMode(value === Translations.onBehalfOther)}
							/>
							{onBehalf ? <Text style={styles.fieldHint}>{Translations.onBehalfEmailHint}</Text> : null}
						</View>
					) : null}

					<View key={`fields-${answersVersion}`}>{renderFormFields(questions)}</View>

					<TouchableOpacity
						disabled={formLoading}
						style={{ width: '100%', alignItems: 'center', justifyContent: 'center' }}
						onPress={sendAndClearForm}
					>
						<View style={styles.button}>
							<Text style={styles.buttontext}>
								{previousSubmissionRef.current && !onBehalf ? Translations.buttonSaveChanges : Translations.buttonSubmitUpper}
							</Text>
							{formLoading && (
								<ActivityIndicator
									color={Colors.white}
									size="small"
									style={{ width: 16, height: 16, marginLeft: Dimens.margin15 }}
								/>
							)}
						</View>
					</TouchableOpacity>

					{previousSubmissionRef.current && !onBehalf && (
						<View style={styles.unsubscribeContainer}>
							<Text style={styles.textLinkHelper}>{Translations.textHinderedQuestion}</Text>
							<TouchableOpacity disabled={isCancelLoading} onPress={cancelSubmission}>
								<View style={styles.unsubscribeButton}>
									<Text style={[styles.textLink, isCancelLoading ? styles.textLinkDisabled : null]}>
										{Translations.textUnsubscribe}
									</Text>
									{isCancelLoading && (
										<ActivityIndicator color={Colors.secondary} size="small" style={{ marginLeft: Dimens.margin8 }} />
									)}
								</View>
							</TouchableOpacity>
						</View>
					)}
				</View>
			)}
			{allowOnBehalf && !isLoading ? (
				<OnBehalfRegistrations eventUuid={eventStore.activeEventUuid} formId={data.id} refreshKey={onBehalfListKey} />
			) : null}
			<KeyboardSpacer />
		</View>
	);
}

export default ContentBlockForm;

const styles = StyleSheet.create({
	formLoading: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	flexFill: {
		flex: 1,
	},
	first: { paddingTop: Dimens.padding0 },
	last: {},
	fieldBlock: {
		// Uniform spacing between every form field, regardless of field type.
		marginBottom: Dimens.margin24,
	},
	inputWrapper: {
		// Only pad below (between input and the underline).
		paddingBottom: Dimens.padding12,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	inputLabel: { fontSize: Dimens.fontSize18, fontFamily: Fonts.montserratRegular, marginBottom: Dimens.margin8 },
	// A locked field should read as already filled in, not as broken: the value stays legible and the hint
	// underneath says where it came from.
	inputLocked: { color: Colors.greyText },
	fieldHint: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		color: Colors.greyText,
		marginTop: Dimens.margin8,
	},
	loggedInAs: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		color: Colors.greyText,
		marginBottom: Dimens.margin12,
	},
	formTitle: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize17,
		paddingHorizontal: Dimens.padding16,
		paddingTop: Dimens.padding20,
		paddingBottom: Dimens.padding16,
		textAlign: 'center',
	},
	// Directly under the title, so the two read as one introduction.
	formIntro: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		color: Colors.greyTextDark,
		paddingHorizontal: Dimens.padding16,
		paddingBottom: Dimens.padding16,
		textAlign: 'center',
	},
	itemstyle: {},
	itemstyleFocussed: {
		borderBottomColor: Colors.secondary,
	},
	itemStyleRatingLabel: {
		marginLeft: Dimens.margin0,
		marginRight: Platform.OS === 'android' ? Dimens.margin0 : -20,
		borderBottomWidth: Platform.OS === 'android' ? 0 : undefined,
		paddingBottom: Platform.OS === 'android' ? Dimens.padding8 : undefined,
	},
	itemStyleRating: {
		marginLeft: Dimens.margin0,
		marginTop: Dimens.margin10,
		paddingBottom: Platform.OS === 'android' ? Dimens.padding8 : Dimens.padding10,
		marginRight: Platform.OS === 'android' ? Dimens.margin0 : -20,
		borderBottomWidth: Platform.OS === 'android' ? 1 : 0,
	},
	input: {
		marginLeft: Dimens.margin0,
		paddingLeft: Dimens.padding0,
		fontFamily: Fonts.montserratRegular,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize17 : Dimens.fontSize16,
		color: Colors.greyTextDark,
		paddingVertical: Platform.OS === 'android' ? Dimens.padding4 : undefined,
	},
	label: {
		color: Colors.greyTextDark,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize15 : Dimens.fontSize16,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratRegular : Fonts.montserratBold,
	},
	labelFocussed: {
		color: Colors.secondary,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize15 : Dimens.fontSize16,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratRegular : Fonts.montserratBold,
	},
	ratingLabel: {
		paddingBottom: 14,
		fontWeight: Platform.OS === 'android' ? 'bold' : undefined,
	},
	buttontext: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
		color: Colors.white,
		paddingLeft: 22,
		paddingRight: 22,
		paddingTop: 17,
		paddingBottom: 17,
	},
	button: {
		backgroundColor: Colors.greyDark,
		marginTop: 50,
		marginBottom: Dimens.margin10,
		minWidth: 130,
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'row',
	},
	smiley: { alignItems: 'center', justifyContent: 'center' },
	smileyIcon: { width: 40, height: 40 },
	unsubscribeContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: Dimens.margin20,
	},
	textLinkHelper: {
		fontSize: Dimens.fontSize14,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
	},
	textLink: {
		fontSize: Dimens.fontSize14,
		color: Colors.secondary,
		fontFamily: Fonts.montserratMedium,
	},
	textLinkDisabled: {
		opacity: 0.4,
	},
	unsubscribeButton: {
		alignItems: 'center',
		flexDirection: 'row',
	},
});
